/**
 * App entry point: builds the static shell, wires the map / sidebar / history modules to the typed
 * API, and handles the radar toggle and history selection. Keeps the
 * orchestration thin — each concern lives in its own module (map.ts, sidebar.ts, history.ts).
 *
 * The map shows LIVE radar detection (GET /api/radar/hail-events) plus the reflectivity overlay;
 * the history sidebar and the click->detail flow read the STORED lead-gen events (GET
 * /api/hail-events, /api/hail-events/:id). Stored swaths are tagged with their event id so a map
 * click can open the right detail.
 */

import {
  ApiError,
  confirmLeads,
  getHailEventDetail,
  getHealth,
  getPartnersStatus,
  getRadarHailEvents,
  getRadarOverlay,
  listHailEvents,
  logout,
  setUnauthorizedHandler,
  syncPartners,
  type HailEventCollection,
  type HailEventSummary,
  type HailFeature,
  type HealthStatus,
  type PartnersStatus,
  type UserProfile,
} from './api.ts';
import { Banner, type Provenance } from './banner.ts';
import { isEmbedded, startHeightReporting } from './embed.ts';
import { attachHelp } from './help.ts';
import { History } from './history.ts';
import { createLoginGate } from './login.ts';
import { HailMap } from './map.ts';
import { Sidebar, type ConfirmInfo } from './sidebar.ts';
import './style.css';

// ČHMÚ publishes a new volume scan every ~5 min and the backend re-polls on its own interval; a
// 60s client refresh keeps the open page current without hammering the API. Map/radar layers skip
// re-applying an identical scan (map.ts), so most ticks are cheap.
const REFRESH_INTERVAL_MS = 60_000;

function buildShell(root: HTMLElement): void {
  root.innerHTML = `
    <div class="layout">
      <header class="app-header">
        <h1>Kroupy — detekce krupobití</h1>
        <span class="spacer"></span>
        <div class="controls">
          <span class="live-status" id="live-status" role="status" aria-live="polite"></span>
          <span class="status" id="status"></span>
          <span class="freshness" id="freshness" role="status" aria-live="polite"></span>
          <span class="partners-synced" id="partners-synced" role="status" aria-live="polite"></span>
          <label><input type="checkbox" id="radar-toggle" /> Radar (odrazivost)</label>
          <button type="button" id="sync-btn">Synchronizovat partnery</button>
          <button type="button" id="help-btn" class="help-btn" aria-label="Nápověda">?</button>
          <span class="user-email" id="user-email"></span>
          <button type="button" id="logout-btn">Odhlásit</button>
        </div>
      </header>
      <aside class="history-panel">
        <h2>Historie zásahů</h2>
        <div id="history"></div>
      </aside>
      <div class="map-wrap">
        <div id="banner"></div>
        <div id="map"></div>
      </div>
      <aside class="sidebar" id="sidebar"></aside>
    </div>`;
}

function el<T extends HTMLElement>(id: string): T {
  const node = document.getElementById(id);
  if (node === null) throw new Error(`missing #${id}`);
  return node as T;
}

/** Tag each stored-event swath polygon with its event_id so a map click can open its detail. */
function storedEventsToFeatureCollection(events: HailEventSummary[]): HailEventCollection {
  return {
    type: 'FeatureCollection',
    metadata: {},
    features: events.map((event) => {
      // exactOptionalPropertyTypes: omit the key entirely rather than set it to undefined.
      const properties: HailFeature['properties'] = { event_id: event.id };
      if (event.hail_size_cm !== null) properties.hail_size_cm = event.hail_size_cm;
      if (event.area_km2 !== null) properties.area_km2 = event.area_km2;
      // Carry the beam-coverage flag so the map can render the 'PODHODNOCENO' cue (F-CONF).
      properties.size_underestimated = event.size_underestimated;
      if (event.size_confidence !== null) properties.size_confidence = event.size_confidence;
      if (event.radar_range_km !== null) properties.radar_range_km = event.radar_range_km;
      return { type: 'Feature', geometry: event.geometry, properties };
    }),
  };
}

async function main(): Promise<void> {
  const root = el<HTMLDivElement>('app');
  if (isEmbedded()) document.body.classList.add('embed');
  buildShell(root);

  // Session gate (ADR-010) — right after buildShell: the shell must exist (#user-email and the
  // logout button live in it, and el() throws on a missing id), and nothing between here and the
  // first tick() touches the network, so no request can fire unauthenticated. The await means the
  // rest of main() needs no early return; body.locked hides the empty shell behind the card.
  const userEmail = el<HTMLSpanElement>('user-email');
  const showProfile = (profile: UserProfile): void => {
    userEmail.textContent = profile.email;
  };
  const gate = createLoginGate((profile) => {
    // Re-login after a mid-run expiry/logout: refresh at once, then resume the paused timer.
    // tick/startTimer are consts declared further down — only ever reached from user events long
    // after main() finished (the same lazy capture openDetail uses for hailMap).
    showProfile(profile);
    void tick().then(() => {
      if (!document.hidden) startTimer();
    });
  });
  showProfile(await gate.ensureLoggedIn());

  const status = el<HTMLSpanElement>('status');
  const setStatus = (text: string): void => {
    status.textContent = text;
  };

  // "Aktualizováno HH:MM:SS" on success; a subtle offline hint on a failed tick (the last good data
  // stays on the map — a failed refresh must never blank it).
  const freshness = el<HTMLSpanElement>('freshness');
  const markFresh = (): void => {
    freshness.textContent = `Aktualizováno ${new Date().toLocaleTimeString('cs-CZ')}`;
    freshness.classList.remove('freshness-offline');
  };
  const markOffline = (): void => {
    freshness.textContent = 'offline / API nedostupné';
    freshness.classList.add('freshness-offline');
  };

  // "Běžím živě" indicator (dot + scan age) from GET /health, which the UI never read before — a
  // stale pipeline looked exactly like a quiet sky (scheduler off for 23 h went unnoticed on
  // 21. 7.). ČHMÚ publishes a scan every ~5 min, so ≤15 min reads as live, ≤60 min as delayed and
  // anything older (or an unreachable /health) as down. Thresholds are deliberately client-side:
  // /health stays a liveness probe and leaves "too old" to its consumers.
  const LIVE_MAX_SECONDS = 15 * 60;
  const DELAYED_MAX_SECONDS = 60 * 60;
  const liveStatus = el<HTMLSpanElement>('live-status');
  liveStatus.title =
    'Stáří posledního zpracovaného snímku radaru ČHMÚ; nový přichází zhruba každých 5 minut.';
  const setLiveStatus = (tone: 'ok' | 'delayed' | 'down', text: string): void => {
    liveStatus.className = `live-status live-${tone}`;
    liveStatus.textContent = text;
  };
  const formatScanAge = (seconds: number): string => {
    const minutes = Math.round(seconds / 60);
    if (minutes < 1) return 'právě teď';
    if (minutes <= 90) return `před ${minutes} min`;
    return `před ${Math.round(minutes / 60)} h`;
  };
  const renderLiveStatus = (health: HealthStatus): void => {
    const age = health.last_scan_age_seconds;
    if (age === null) {
      setLiveStatus('delayed', 'Čekám na první sken radaru');
    } else if (age <= LIVE_MAX_SECONDS) {
      setLiveStatus('ok', `Živě · sken ${formatScanAge(age)}`);
    } else if (age <= DELAYED_MAX_SECONDS) {
      setLiveStatus('delayed', `Zpoždění · sken ${formatScanAge(age)}`);
    } else {
      setLiveStatus('down', `Neaktuální · sken ${formatScanAge(age)}`);
    }
  };

  // "Partneři: naposledy synchronizováno <čas>" — a subtle line the partner-sync button + each
  // refresh keep current. Never synced yet reads "—" rather than a fabricated time.
  const partnersSynced = el<HTMLSpanElement>('partners-synced');
  const renderPartnersSynced = (status: PartnersStatus): void => {
    const when =
      status.last_synced_at === null
        ? '—'
        : new Date(status.last_synced_at).toLocaleString('cs-CZ');
    partnersSynced.textContent = `Partneři: naposledy synchronizováno ${when}`;
  };

  // A running confirmation owns the header status line: the 60s tick (loadStoredEvents) must not
  // overwrite "Hledám servisy…" with the stored-events count mid-search.
  // Counter, not a boolean: two confirms can overlap (a second event opened mid-search), and
  // the first one finishing must not unfreeze the status line while the second still runs.
  let confirmsRunning = 0;

  // Sidebar is built before hailMap; its callbacks only fire on user interaction, by which time
  // hailMap (a const declared below) is initialized — same lazy-capture as openDetail.
  // The confirm callback is THE human gate (S320/S335): it is the only client action that spends
  // Places budget, and it re-renders the detail so the fresh leads land on the map + list.
  const sidebar = new Sidebar(
    el('sidebar'),
    (shopKey) => hailMap.focusShop(shopKey),
    () => {
      if (openEventId === null) return;
      const id = openEventId;
      confirmsRunning += 1;
      // The panel says what is happening AND how long it can take — a tiled rural search runs
      // minutes, and a user who closes the tab mid-search spends the budget without the result.
      let reopenDetail = true;
      setStatus('Hledám servisy (Google Places)…');
      sidebar.showLoading('Hledám servisy — může to trvat 1–2 minuty, nezavírejte prosím stránku.');
      confirmLeads(id)
        .then((report) => {
          // The note (a fallback that could not fully run, e.g. daily budget exhausted) beats the
          // happy-path counts — the human must learn WHY the list is short and that tomorrow works.
          if (report.note) {
            setStatus(`Places: ${report.leads_found} servisů — ${report.note}`);
          } else {
            const zone = report.zone_expanded ? ', rozšířená zóna' : '';
            setStatus(
              `Places: ${report.leads_found} servisů (${report.api_calls} API volání${zone})`,
            );
          }
          confirmInfoByEvent.set(id, { zone_expanded: report.zone_expanded, note: report.note });
        })
        .catch((err: unknown) => {
          if (err instanceof ApiError && err.status === 503) {
            // A missing Places key is a config problem only an admin can fix — it must survive in
            // the panel, not flash by on the status line and vanish under the next tick.
            sidebar.showError(
              'Vyhledávání servisů není na serveru nastaveno — chybí klíč ke Google Places. ' +
                'Kontaktujte správce.',
            );
            reopenDetail = false;
            setStatus('Vyhledání servisů selhalo — server není nastaven.');
          } else if (err instanceof ApiError && err.status === 413) {
            setStatus(
              'Zasažená oblast je pro vyhledání servisů příliš velká. Kontaktujte správce.',
            );
          } else if (err instanceof ApiError && err.status === 429) {
            // The backend's 429 detail is user-ready Czech and knows WHICH limit tripped — daily
            // budget ("obnoví se zítra"), monthly budget, or Google's per-minute quota ("počkejte
            // minutu"). A hardcoded "obnoví se zítra" here would overwrite that truth with wrong
            // advice for two of the three cases. errorDetail() already propagated the detail into
            // err.message; the generic status-line text only appears when the body was unparsable.
            setStatus(err.message);
          } else if (err instanceof ApiError && err.status === 502) {
            // The 502 detail is a raw English upstream error — log it, show Czech to the client.
            console.error(err);
            setStatus('Google Places je dočasně nedostupné — zkuste to znovu za chvíli.');
          } else {
            setStatus(err instanceof Error ? err.message : 'Vyhledání servisů selhalo');
          }
        })
        .finally(() => {
          confirmsRunning -= 1;
          // Re-render only when the user is still on this event — they may have opened another
          // detail during the long search, and that view must not be clobbered.
          if (reopenDetail && openEventId === id) openDetail(id);
        });
    },
  );
  sidebar.showPlaceholder();

  const banner = new Banner(el('banner'));

  // id -> swath geometry of the currently loaded stored events, so picking an event (from the map,
  // history, or after a seed) can frame it without an extra fetch.
  const geometryById = new Map<number, GeoJSON.Polygon>();

  // The event whose detail the sidebar currently shows — the confirm-leads gate needs to know which
  // event the button belongs to. Null until the first detail opens.
  let openEventId: number | null = null;

  // Per-event outcome of a confirm made in THIS session (zone expansion / budget note), so the
  // sidebar can keep showing it after the detail re-renders — the header status line alone gets
  // overwritten by the periodic refresh.
  const confirmInfoByEvent = new Map<number, ConfirmInfo>();

  const openDetail = (id: number): void => {
    openEventId = id;
    // Opening a detail — from the history list OR a map click — is the single "seen" funnel, so
    // the 'nový' badge clears no matter which way the user got there.
    history.markSeen(id);
    const geometry = geometryById.get(id);
    if (geometry) hailMap.fitToPolygon(geometry);
    sidebar.showLoading();
    getHailEventDetail(id)
      .then((detail) => {
        // A slower response for a previously clicked event must not overwrite the detail the user
        // opened after it — only the CURRENT event may render (out-of-order guard).
        if (openEventId !== id) return;
        sidebar.showEvent(detail, confirmInfoByEvent.get(id));
        // Review-only (underestimated) partners still show on the MAP alongside normal matched
        // partners. One of the two partner lists is always empty (mutually exclusive per event).
        hailMap.setShops([...detail.partner_alerts, ...detail.review_only_partners], detail.leads);
        // Predicted future swaths (F-4B); empty when the cell has no movement vector yet.
        hailMap.setForecast(detail.forecast?.predicted_swaths ?? []);
      })
      .catch((err: unknown) => {
        if (openEventId !== id) return;
        sidebar.showError(err instanceof Error ? err.message : String(err));
        hailMap.setShops([], []);
        hailMap.setForecast([]);
      });
  };

  // openDetail captures hailMap, but is only ever invoked from later user/async events, so the
  // const is initialized before any call. Declared here to keep the click handlers above readable.
  const hailMap = new HailMap(el('map'), openDetail);
  const history = new History(el('history'), openDetail);

  // --- Radar overlay + toggle ---
  const radarToggle = el<HTMLInputElement>('radar-toggle');
  radarToggle.addEventListener('change', () => hailMap.toggleRadar(radarToggle.checked));

  const loadRadar = async (): Promise<void> => {
    try {
      const overlay = await getRadarOverlay();
      hailMap.setRadarOverlay(overlay);
      radarToggle.disabled = overlay === null;
      if (overlay === null) {
        radarToggle.checked = false;
        hailMap.toggleRadar(false);
      }
    } catch {
      radarToggle.disabled = true;
    }
  };

  // --- Stored events (history + clickable swaths) ---
  // Tracks whether the loaded swaths are real detections or only the synthetic demo, so the banner
  // can state the right caveat. Set by loadStoredEvents (sources of stored events) and refined by
  // loadLiveDetection (which can prove a live detection exists).
  let storedAreSynthetic = false;
  let liveHail = false;

  const loadStoredEvents = async (): Promise<void> => {
    const events = await listHailEvents();
    geometryById.clear();
    for (const event of events) geometryById.set(event.id, event.geometry);
    // "Synthetic" iff there are events and every one of them is demo provenance.
    storedAreSynthetic =
      events.length > 0 && events.every((event) => event.isotherm_source === 'synthetic');
    history.render(events);
    hailMap.setHailEvents(storedEventsToFeatureCollection(events));
    if (confirmsRunning === 0) setStatus(`${events.length} uložených zásahů`);
  };

  // Live detection is informational here; failure must not blank the whole UI.
  const loadLiveDetection = async (): Promise<void> => {
    try {
      const live = await getRadarHailEvents();
      liveHail = live.features.length > 0;
    } catch {
      liveHail = false; // live detection is optional context; the stored-event flow is the product
    }
  };

  // Decide what the banner should say from what we actually loaded: a current live detection wins;
  // otherwise synthetic-only stored events are flagged as demo; otherwise it is the clear-sky note.
  const provenance = (): Provenance => {
    if (liveHail) return 'live';
    if (storedAreSynthetic) return 'synthetic';
    return 'none';
  };

  // Partner freshness is informational; a failed read must not blank the rest of the refresh.
  const loadPartnersSynced = async (): Promise<void> => {
    try {
      renderPartnersSynced(await getPartnersStatus());
    } catch {
      // leave the last good "naposledy synchronizováno" line in place
    }
  };

  // Unlike the loaders above this one renders its own failure state: an unreachable /health IS the
  // "down" signal the indicator exists to show, not something to leave stale on screen.
  const loadLiveStatus = async (): Promise<void> => {
    try {
      renderLiveStatus(await getHealth());
    } catch {
      setLiveStatus('down', 'Stav serveru nedostupný');
    }
  };

  const refresh = async (): Promise<void> => {
    await Promise.all([
      loadRadar(),
      loadStoredEvents(),
      loadLiveDetection(),
      loadPartnersSynced(),
      loadLiveStatus(),
    ]);
    banner.show(provenance());
  };

  // --- Partner-pool freshness sync (F-PRT-SYNC) ---
  const syncBtn = el<HTMLButtonElement>('sync-btn');
  syncBtn.addEventListener('click', () => {
    syncBtn.disabled = true;
    setStatus('Synchronizuji partnery…');
    syncPartners()
      .then(async (report) => {
        // Compact diff in the header, e.g. "Partneři: +3 / ~2 / −1 (164 aktivních)".
        setStatus(
          `Partneři: +${report.added} / ~${report.updated} / −${report.deactivated} ` +
            `(${report.total_active} aktivních)`,
        );
        renderPartnersSynced(report);
        await refresh();
      })
      .catch((err: unknown) =>
        setStatus(err instanceof Error ? err.message : 'Synchronizace selhala'),
      )
      .finally(() => {
        syncBtn.disabled = false;
      });
  });

  // --- Logout (ADR-010) — wired like the sync button ---
  const logoutBtn = el<HTMLButtonElement>('logout-btn');
  logoutBtn.addEventListener('click', () => {
    logoutBtn.disabled = true;
    logout()
      .then(() => {
        stopTimer(); // no point polling into 401s while the gate is up
        userEmail.textContent = '';
        gate.open();
      })
      .catch((err: unknown) => setStatus(err instanceof Error ? err.message : 'Odhlášení selhalo'))
      .finally(() => {
        logoutBtn.disabled = false;
      });
  });

  const stopHeightReporting = startHeightReporting();
  const detachHelp = attachHelp(el<HTMLButtonElement>('help-btn'));

  // One refresh + UI freshness update. Swallows the error into the offline hint (keeping the last
  // good data on the map) rather than throwing, so a flaky tick never tears down the timer loop.
  const tick = async (): Promise<void> => {
    try {
      await refresh();
      markFresh();
    } catch (err: unknown) {
      markOffline();
      // status reflects the failure too, but only before the first successful load left a count.
      if (status.textContent === '') {
        setStatus(err instanceof Error ? err.message : 'Načtení selhalo');
      }
    }
  };

  // --- Live refresh on a timer, paused while the tab is hidden ---
  // A backgrounded tab should not keep polling; on return we resume the timer and refresh at once so
  // the user never stares at stale data while it waits for the next tick.
  let timer: number | null = null;
  const startTimer = (): void => {
    if (timer !== null) return;
    timer = window.setInterval(() => void tick(), REFRESH_INTERVAL_MS);
  };
  const stopTimer = (): void => {
    if (timer === null) return;
    window.clearInterval(timer);
    timer = null;
  };
  const onVisibility = (): void => {
    if (document.hidden) {
      stopTimer();
    } else {
      void tick();
      startTimer();
    }
  };
  document.addEventListener('visibilitychange', onVisibility);

  // Clean up on teardown (HMR / page unload) so no interval or listener leaks across reloads.
  const teardown = (): void => {
    stopTimer();
    document.removeEventListener('visibilitychange', onVisibility);
    window.removeEventListener('pagehide', teardown);
    stopHeightReporting();
    detachHelp();
    setUnauthorizedHandler(null);
    gate.teardown();
  };
  window.addEventListener('pagehide', teardown);
  if (import.meta.hot) import.meta.hot.dispose(teardown);

  // Session expiry mid-run: a tick fires five requests in parallel, so five 401s can land at
  // once. stopTimer() halts further polling and gate.open() is idempotent, so exactly one login
  // card appears; its callback (top of main) refreshes and restarts the timer after re-login.
  // Registered only now — during the initial gate ensureLoggedIn handles its own 401.
  setUnauthorizedHandler(() => {
    stopTimer();
    gate.open();
  });

  await tick();
  if (!document.hidden) startTimer();
}

void main();
